import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import UserProgressContext from "../../userProgressContext";

const CHARACTER_COLOURS = {
  Alex:       '#818cf8',
  Emma:       '#34d399',
  Grizzy:     '#fb923c',
  '[Player]': '#fb923c',
  Jordan:     '#2dd4bf',
};

// Seconds between each bubble reveal in a multi-line batch
const READING_DELAY_MS = 1.2;

// Normalise both step formats into a unified lines array
function normaliseLines(step) {
  if (!step) return [];
  if (step.dialogues && Array.isArray(step.dialogues)) {
    return step.dialogues;
  }
  if (step.character && step.dialogue) {
    return [{ character: step.character, dialogue: step.dialogue }];
  }
  return [];
}

// ─── Single-line layout: large portrait + speech bubble (legacy Conversations style) ───
const SingleLineLayout = ({ line, name }) => {
  const colour = CHARACTER_COLOURS[line.character] ?? '#ffffff';
  const portraitChar = line.character === '[Player]' ? 'Grizzy' : line.character;
  const displayName = line.character === '[Player]' ? name : line.character;

  return (
    <div className="flex flex-col md:flex-row items-center bg-gray-900 p-3 min-[1024px]:p-8 rounded-lg shadow-lg w-full h-full">
      <motion.div
        key={line.character}
        initial={{ x: 60, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        className="flex flex-col justify-center items-center gap-1 md:mb-0 md:mr-6"
      >
        <div
          className="w-36 h-36 md:w-48 md:h-48 rounded-full border-4 animate-pulseShadow overflow-hidden"
          style={{
            borderColor: colour,
            '--glow-color': colour,
          }}
        >
          <img
            src={require(`../../static/images/characters/${portraitChar}.png`)}
            alt={displayName}
            className="w-full h-full object-cover rounded-full"
          />
        </div>
        <span className="text-xl">{displayName}</span>
      </motion.div>
      <div className="relative bg-white w-full text-gray-900 border-2 border-gray-700 rounded-lg p-4 md:p-6 mt-2 md:mt-0 shadow-xl">
        <p className="text-lg md:text-2xl leading-relaxed">
          {line.dialogue.replace('[Player]', name)}
        </p>
        <div className="block md:absolute left-[-12px] min-[1024px]:left-[-16px] top-8 w-0 h-0 border-t-8 border-b-8 border-r-8 border-t-transparent border-b-transparent border-r-white" />
      </div>
    </div>
  );
};

// ─── Multi-line layout: staggered chat bubbles ───
const MultiLineLayout = ({ lines, name, step, seenKey, setNextButtonLocked }) => {
  const wasAlreadySeen = sessionStorage.getItem(seenKey) === '1';

  const [activeBubbleIndex, setActiveBubbleIndex] = useState(
    wasAlreadySeen ? lines.length - 1 : 0
  );
  const [allRevealed, setAllRevealed] = useState(wasAlreadySeen);
  const [revealAll, setRevealAll] = useState(wasAlreadySeen);
  const timerIdsRef = useRef([]);
  const scrollRef = useRef(null);

  // Primary speaker: first character in the batch → left-aligned
  const primarySpeaker = lines[0]?.character || 'Alex';

  // Portrait shown only at speaker-change boundaries; guarded against out-of-bounds
  const showPortrait = (i) =>
    i === 0 || (i < lines.length && lines[i].character !== lines[i - 1].character);

  // Walk back to find the portrait that owns the active bubble
  let activePortraitIndex = Math.min(activeBubbleIndex, lines.length - 1);
  while (activePortraitIndex > 0 && !showPortrait(activePortraitIndex)) {
    activePortraitIndex--;
  }

  // Lock Next on mount
  useEffect(() => {
    if (!wasAlreadySeen) setNextButtonLocked(true);
    return () => timerIdsRef.current.forEach(clearTimeout);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Schedule bubble advances + allRevealed
  useEffect(() => {
    if (wasAlreadySeen) return;

    timerIdsRef.current.forEach(clearTimeout);
    timerIdsRef.current = [];

    lines.forEach((_, i) => {
      if (i === 0) return;
      const id = setTimeout(() => setActiveBubbleIndex(i), i * READING_DELAY_MS * 1000);
      timerIdsRef.current.push(id);
    });

    const revealDelay = ((lines.length - 1) * READING_DELAY_MS + 0.3) * 1000;
    const id = setTimeout(() => {
      setAllRevealed(true);
      setNextButtonLocked(false);
      sessionStorage.setItem(seenKey, '1');
    }, revealDelay);
    timerIdsRef.current.push(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to bottom whenever a new bubble becomes active
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [activeBubbleIndex]);

  const handleClick = () => {
    if (allRevealed) return;
    timerIdsRef.current.forEach(clearTimeout);
    timerIdsRef.current = [];
    setRevealAll(true);
    setActiveBubbleIndex(lines.length - 1);
    setAllRevealed(true);
    setNextButtonLocked(false);
    sessionStorage.setItem(seenKey, '1');
  };

  return (
    <div
      ref={scrollRef}
      className="flex flex-col gap-3 w-full h-full overflow-y-auto cursor-pointer select-none"
      onClick={handleClick}
    >
      {/* Inline visuals */}
      {step.inlineVisuals && (
        <div className="flex flex-wrap justify-center gap-4 mb-2">
          {step.inlineVisuals.map((visual, id) => (
            <div key={id} className="w-full h-64 rounded-lg bg-white flex items-center p-2">
              <img
                src={require(`../../static/images/visuals/${visual}.png`)}
                alt="Visual"
                className="hidden min-[640px]:block w-full h-full object-contain rounded-lg"
              />
              <img
                src={require(`../../static/images/visuals/${visual}_mobile.png`)}
                alt="Visual"
                className="block min-[640px]:hidden w-full h-full object-contain rounded-lg"
              />
            </div>
          ))}
        </div>
      )}

      {lines.map((line, i) => {
        const isLeft = line.character === primarySpeaker;
        const colour = CHARACTER_COLOURS[line.character] ?? '#ffffff';
        const displayName = line.character === '[Player]' ? name : line.character;
        const portraitChar = line.character === '[Player]' ? 'Grizzy' : line.character;
        const portraitVisible = showPortrait(i);
        const isPortraitActive = portraitVisible && i === activePortraitIndex;

        return (
          <motion.div
            key={i}
            initial={wasAlreadySeen ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={
              revealAll
                ? { duration: 0 }
                : { delay: i * READING_DELAY_MS, duration: 0.25, ease: 'easeOut' }
            }
            className={`flex items-end gap-2 ${isLeft ? 'flex-row' : 'flex-row-reverse'}`}
          >
            {/* Portrait — 48×48, circle-cropped, shown at speaker-change boundaries only */}
            <div className="flex-shrink-0 w-12 h-12">
              {portraitVisible ? (
                <motion.div
                  animate={{
                    borderColor: isPortraitActive ? colour : 'rgba(100,100,100,0.3)',
                    opacity: isPortraitActive ? 1 : 0.5,
                  }}
                  transition={{ duration: 0.15 }}
                  className="w-12 h-12 rounded-full border-2 overflow-hidden"
                >
                  <img
                    src={require(`../../static/images/characters/${portraitChar}.png`)}
                    alt={displayName}
                    className="w-full h-full object-cover"
                  />
                </motion.div>
              ) : (
                <div className="w-12 h-12" />
              )}
            </div>

            {/* Speech bubble */}
            <div
              className={`relative max-w-[75%] rounded-2xl px-4 py-3 text-sm md:text-base leading-relaxed shadow-md ${
                isLeft
                  ? 'bg-gray-800 text-white rounded-bl-sm'
                  : 'bg-indigo-900 text-white rounded-br-sm'
              }`}
            >
              {portraitVisible && (
                <span
                  className="block text-xs font-semibold mb-1 opacity-75"
                  style={{ color: colour }}
                >
                  {displayName}
                </span>
              )}
              {line.dialogue.replace('[Player]', name)}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

// ─── Main component ───
const DialogueBatch = ({ step }) => {
  const { name, localProgress, setNextButtonLocked } = useContext(UserProgressContext);
  const lines = useMemo(() => normaliseLines(step), [step]);
  const seenKey = `seenDialogue_${localProgress}`;

  // Single-line: unlock Next immediately (no stagger needed)
  useEffect(() => {
    if (lines.length === 1) {
      setNextButtonLocked(false);
    }
  }, [lines.length, setNextButtonLocked]);

  if (lines.length === 0) return null;

  if (lines.length === 1) {
    return <SingleLineLayout line={lines[0]} name={name} />;
  }

  return (
    <MultiLineLayout
      lines={lines}
      name={name}
      step={step}
      seenKey={seenKey}
      setNextButtonLocked={setNextButtonLocked}
    />
  );
};

export default DialogueBatch;
